import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { bleManager, BLEManager, BLE_COMMANDS } from "../config/bleConfig";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, AreaChart, Area
} from "recharts";
import {
  Database, Search, Bluetooth, Activity, ChevronDown, ChevronRight,
  Zap, Battery, Gauge, Thermometer, Wind, Car, Radio, Terminal,
  Send, Play, Pause, Trash2, Download, Filter
} from "lucide-react";

const CATEGORY_META = {
  battery: { icon: Battery, color: "#2dd4a8", label: "Battery / BMS" },
  drive: { icon: Gauge, color: "#4a9eff", label: "Drive Unit" },
  suspension: { icon: Car, color: "#f5a623", label: "Suspension" },
  climate: { icon: Thermometer, color: "#e84057", label: "Climate / Thermal" },
  charging: { icon: Zap, color: "#30D158", label: "Charging" },
  steering: { icon: Radio, color: "#a78bfa", label: "Steering" },
  body: { icon: Car, color: "#6b7280", label: "Body / Doors" },
  safety: { icon: Activity, color: "#FF3B30", label: "Safety" },
  other: { icon: Database, color: "#6b7280", label: "Other" },
};

const CHART_COLORS = ["#2dd4a8", "#4a9eff", "#f5a623", "#e84057", "#a78bfa", "#30D158"];

export default function CANDecoder() {
  const [dbSummary, setDbSummary] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [expandedCat, setExpandedCat] = useState(null);
  const [expandedMsg, setExpandedMsg] = useState(null);

  // Manual decoder
  const [manualId, setManualId] = useState("0x10B");
  const [manualData, setManualData] = useState("96 91 8C 87 00");
  const [decodeResult, setDecodeResult] = useState(null);

  // Live stream
  const [streaming, setStreaming] = useState(false);
  const [bleConnected, setBleConnected] = useState(false);
  const [liveFrames, setLiveFrames] = useState([]);
  const [trackedSignals, setTrackedSignals] = useState([]);
  const [chartData, setChartData] = useState([]);
  const streamRef = useRef(false);
  const frameCountRef = useRef(0);

  // Recording state
  const [recording, setRecording] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [sessions, setSessions] = useState([]);

  // Load database + sessions
  useEffect(() => {
    axios.get(`${API}/can/summary`).then(r => setDbSummary(r.data)).catch(() => {});
    axios.get(`${API}/can/catalog`).then(r => setCatalog(r.data)).catch(() => {});
    axios.get(`${API}/can/sessions`).then(r => setSessions(r.data)).catch(() => {});
    axios.get(`${API}/can/session/active`).then(r => {
      if (r.data.active) { setRecording(true); setActiveSession(r.data); }
    }).catch(() => {});
  }, []);

  // Search
  useEffect(() => {
    if (searchQuery.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(() => {
      axios.get(`${API}/can/search?q=${encodeURIComponent(searchQuery)}`).then(r => setSearchResults(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // BLE live stream
  useEffect(() => {
    if (!BLEManager.isSupported()) return;

    bleManager.onData((data) => {
      if (!streamRef.current) return;
      frameCountRef.current++;

      // The BLE firmware sends pre-decoded JSON from CAN reads
      // Add to live frames list
      setLiveFrames(prev => {
        const newFrame = {
          id: frameCountRef.current,
          ts: new Date().toLocaleTimeString(),
          ...data
        };
        return [...prev.slice(-200), newFrame];
      });

      // Update chart data with key values
      setChartData(prev => {
        const point = { time: new Date().toLocaleTimeString() };
        if (data.v !== undefined) point.packV = data.v;
        if (data.i !== undefined) point.packI = data.i;
        if (data.soc !== undefined) point.soc = data.soc;
        if (data.bt !== undefined) point.battTemp = data.bt;
        if (data.spd !== undefined) point.speed = data.spd;
        if (data["12v"] !== undefined) point.aux12v = data["12v"];
        return [...prev.slice(-120), point];
      });
    });

    bleManager.onDisconnect(() => {
      setBleConnected(false);
      setStreaming(false);
      streamRef.current = false;
    });
  }, []);

  const connectBLE = useCallback(async () => {
    try {
      await bleManager.connect();
      setBleConnected(true);
      toast.success("ESP32 BLE connected");
    } catch (e) {
      toast.error("BLE: " + e.message);
    }
  }, []);

  const toggleStream = useCallback(() => {
    const next = !streaming;
    setStreaming(next);
    streamRef.current = next;
    if (next) toast.success("Live CAN stream started");
  }, [streaming]);

  // Recording controls
  const startRecording = useCallback(async () => {
    try {
      const name = `CAN Session ${new Date().toLocaleString()}`;
      const resp = await axios.post(`${API}/can/session/start`, { name });
      setRecording(true);
      setActiveSession(resp.data);
      toast.success("Recording started: " + resp.data.name);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start recording");
    }
  }, []);

  const stopRecording = useCallback(async () => {
    try {
      const resp = await axios.post(`${API}/can/session/stop`);
      setRecording(false);
      setActiveSession(null);
      toast.success(`Session saved: ${resp.data.frame_count} frames`);
      // Refresh sessions list
      const s = await axios.get(`${API}/can/sessions`);
      setSessions(s.data);
    } catch (e) {
      toast.error("Failed to stop recording");
    }
  }, []);

  const exportSession = useCallback((sessionId, fmt) => {
    window.open(`${API}/can/session/${sessionId}/export?fmt=${fmt}`, "_blank");
  }, []);

  const deleteSession = useCallback(async (sessionId) => {
    if (!window.confirm("Delete this CAN session?")) return;
    try {
      await axios.delete(`${API}/can/session/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      toast.success("Session deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  }, []);

  const replaySession = useCallback(async (sessionId) => {
    try {
      const resp = await axios.get(`${API}/can/session/${sessionId}/frames?limit=500`);
      const frames = resp.data;
      if (!frames.length) { toast.info("No frames in session"); return; }

      // Build chart data from session frames
      const replayChartData = frames.map(f => {
        const point = { time: `${(f.ts / 1000).toFixed(1)}s` };
        const sigs = f.signals || {};
        // Map known signals
        for (const [name, sig] of Object.entries(sigs)) {
          point[name] = sig.value;
        }
        return point;
      });

      setChartData(replayChartData);
      setLiveFrames(frames.map((f, i) => ({
        id: i,
        ts: `${(f.ts / 1000).toFixed(1)}s`,
        can_id_hex: f.can_id_hex,
        message_name: f.message_name,
        signals: f.signals,
      })));
      toast.success(`Replaying ${frames.length} frames`);
    } catch (e) {
      toast.error("Replay failed");
    }
  }, []);

  // Manual decode
  const handleDecode = useCallback(async () => {
    try {
      const id = parseInt(manualId);
      const data = manualData.trim().split(/[\s,]+/).map(v => parseInt(v, 16));
      const resp = await axios.post(`${API}/can/decode`, { id, data });
      setDecodeResult(resp.data);
    } catch (e) {
      toast.error("Decode failed: " + (e.response?.data?.detail || e.message));
    }
  }, [manualId, manualData]);

  // Track a signal on chart
  const toggleTrack = useCallback((sigName) => {
    setTrackedSignals(prev =>
      prev.includes(sigName) ? prev.filter(s => s !== sigName) : [...prev.slice(-5), sigName]
    );
  }, []);

  return (
    <div style={{ padding: "20px 24px", maxWidth: 1600, margin: "0 auto" }}>
      <style>{`
        .dcard { background:#0e0e16; border:1px solid #1a1a28; border-radius:10px; padding:20px; transition:border-color .2s; }
        .dcard:hover { border-color:#2a2a3e; }
        .dtitle { font-size:11px; text-transform:uppercase; letter-spacing:2px; color:#6b7280; margin-bottom:14px; display:flex; align-items:center; gap:8px; }
        .dinput { font-family:inherit; font-size:12px; padding:8px 12px; border-radius:6px; border:1px solid #1e1e2e; background:#0a0a10; color:#e2e8f0; outline:none; transition:border-color .15s; }
        .dinput:focus { border-color:#4a9eff; }
        .dbtn { font-size:11px; padding:7px 14px; border-radius:5px; border:1px solid #1e1e2e; background:#14141e; color:#e2e8f0; cursor:pointer; font-weight:600; letter-spacing:.5px; transition:all .15s; display:inline-flex; align-items:center; gap:6px; }
        .dbtn:hover { background:#1c1c2a; border-color:#3a3a4e; }
        .dbtn:disabled { opacity:0.4; cursor:not-allowed; }
        .dbtn-b { border-color:rgba(74,158,255,.3); color:#4a9eff; }
        .dbtn-g { border-color:rgba(45,212,168,.3); color:#2dd4a8; }
        .dbtn-r { border-color:rgba(232,64,87,.3); color:#e84057; }
        .sig-row { display:flex; align-items:center; gap:10px; padding:6px 10px; border-radius:5px; font-size:12px; transition:background .1s; }
        .sig-row:hover { background:rgba(255,255,255,0.03); }
        .sig-tag { font-size:9px; padding:2px 6px; border-radius:3px; font-weight:700; letter-spacing:.5px; }
        .cat-btn { display:flex; align-items:center; gap:10px; padding:10px 14px; width:100%; cursor:pointer; border-radius:8px; transition:background .15s; border:none; background:none; text-align:left; }
        .cat-btn:hover { background:rgba(255,255,255,0.03); }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: "#e2e8f0", letterSpacing: 1, marginBottom: 4 }}>CAN Signal Decoder</h1>
          <p style={{ fontSize: 12, color: "#6b7280" }}>
            {dbSummary ? `${dbSummary.total_messages} messages / ${dbSummary.total_signals} signals across ${dbSummary.buses.join(", ")} buses` : "Loading database..."}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Recording indicator */}
          {recording && (
            <span style={{ fontSize: 11, color: "#e84057", display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#e84057", animation: "pulse 1s infinite" }} />
              REC ({activeSession?.frame_count || 0} frames)
            </span>
          )}
          {!recording ? (
            <button className="dbtn dbtn-r" onClick={startRecording} data-testid="start-record-btn" style={{borderColor:"rgba(232,64,87,.3)", color:"#e84057"}}>
              <span style={{width:8,height:8,borderRadius:"50%",background:"#e84057"}} /> Record
            </button>
          ) : (
            <button className="dbtn" onClick={stopRecording} data-testid="stop-record-btn" style={{borderColor:"rgba(232,64,87,.3)", color:"#e84057"}}>
              <Pause size={12} /> Stop
            </button>
          )}
          {bleConnected ? (
            <>
              <span style={{ fontSize: 11, color: streaming ? "#2dd4a8" : "#6b7280" }}>
                {streaming ? `Streaming (${liveFrames.length} frames)` : "Connected"}
              </span>
              <button className={`dbtn ${streaming ? "dbtn-r" : "dbtn-g"}`} onClick={toggleStream} data-testid="toggle-stream-btn">
                {streaming ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Stream</>}
              </button>
            </>
          ) : (
            <button className="dbtn dbtn-b" onClick={connectBLE} disabled={!BLEManager.isSupported()} data-testid="ble-connect-decoder-btn">
              <Bluetooth size={12} /> {BLEManager.isSupported() ? "Pair ESP32" : "No BLE Support"}
            </button>
          )}
        </div>
      </div>

      {/* Top Row: Search + Manual Decoder */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* Search */}
        <div className="dcard" data-testid="signal-search-card">
          <div className="dtitle"><Search size={14} color="#4a9eff" /> Signal Search</div>
          <input
            className="dinput"
            style={{ width: "100%", marginBottom: 12 }}
            placeholder="Search signals... (e.g. voltage, temperature, speed)"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            data-testid="signal-search-input"
          />
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {searchResults.length > 0 ? searchResults.map((sig, i) => (
              <div key={i} className="sig-row">
                <span style={{ color: "#4a9eff", fontFamily: "monospace", width: 50 }}>{sig.message_id}</span>
                <span style={{ color: "#e2e8f0", flex: 1 }}>{sig.name}</span>
                <span style={{ color: "#6b7280", fontSize: 10 }}>{sig.unit || "--"}</span>
                <span className="sig-tag" style={{ background: "rgba(74,158,255,0.1)", color: "#4a9eff" }}>{sig.bus}</span>
              </div>
            )) : searchQuery.length >= 2 ? (
              <p style={{ color: "#6b7280", fontSize: 12, textAlign: "center", padding: 20 }}>No signals found</p>
            ) : (
              <p style={{ color: "#6b7280", fontSize: 12, textAlign: "center", padding: 20 }}>Type at least 2 characters to search</p>
            )}
          </div>
        </div>

        {/* Manual Decoder */}
        <div className="dcard" data-testid="manual-decoder-card">
          <div className="dtitle"><Terminal size={14} color="#f5a623" /> Frame Decoder</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
            <span style={{ fontSize: 11, color: "#6b7280" }}>ID:</span>
            <input className="dinput" style={{ width: 80 }} value={manualId} onChange={e => setManualId(e.target.value)} data-testid="manual-can-id" />
            <span style={{ fontSize: 11, color: "#6b7280" }}>Data (hex):</span>
            <input className="dinput" style={{ flex: 1 }} value={manualData} onChange={e => setManualData(e.target.value)} placeholder="96 91 8C 87 00" data-testid="manual-can-data" />
            <button className="dbtn dbtn-b" onClick={handleDecode} data-testid="decode-btn"><Send size={11} /> Decode</button>
          </div>
          {decodeResult && (
            <div style={{ maxHeight: 180, overflowY: "auto" }}>
              {decodeResult.known ? (
                <>
                  <div style={{ fontSize: 11, color: "#2dd4a8", marginBottom: 6 }}>
                    {decodeResult.messages?.map(m => `${m.name} [${m.bus}] from ${m.origin}`).join(", ")}
                  </div>
                  {Object.entries(decodeResult.signals).map(([name, sig]) => (
                    <div key={name} className="sig-row">
                      <span style={{ color: "#e2e8f0", flex: 1, fontWeight: 600 }}>{name}</span>
                      <span style={{ color: "#2dd4a8", fontFamily: "monospace", fontSize: 14, fontWeight: 700 }}>{sig.value}</span>
                      <span style={{ color: "#6b7280", fontSize: 11 }}>{sig.unit}</span>
                      <span style={{ color: "#4a4a5e", fontSize: 10, fontFamily: "monospace" }}>raw:{sig.raw}</span>
                      {sig.enum && <span className="sig-tag" style={{ background: "rgba(245,166,35,0.1)", color: "#f5a623" }}>{sig.enum}</span>}
                    </div>
                  ))}
                </>
              ) : (
                <p style={{ color: "#e84057", fontSize: 12 }}>Unknown CAN ID: {decodeResult.id}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Live Charts (only when streaming) */}
      {chartData.length > 2 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div className="dcard" data-testid="voltage-chart">
            <div className="dtitle"><Battery size={14} color="#2dd4a8" /> Pack Voltage & SOC</div>
            <div style={{ height: 180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="gVolt" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2dd4a8" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#2dd4a8" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="time" stroke="#3a3a4e" tick={{ fontSize: 9, fill: "#6b7280" }} />
                  <YAxis stroke="#3a3a4e" tick={{ fontSize: 9, fill: "#6b7280" }} />
                  <Tooltip contentStyle={{ background: "#0e0e16", border: "1px solid #1a1a28", borderRadius: 6, fontSize: 11 }} />
                  <Area type="monotone" dataKey="packV" stroke="#2dd4a8" fill="url(#gVolt)" strokeWidth={2} dot={false} name="Pack V" />
                  <Line type="monotone" dataKey="soc" stroke="#4a9eff" strokeWidth={2} dot={false} name="SOC %" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="dcard" data-testid="temp-chart">
            <div className="dtitle"><Thermometer size={14} color="#e84057" /> Temperature & Current</div>
            <div style={{ height: 180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="time" stroke="#3a3a4e" tick={{ fontSize: 9, fill: "#6b7280" }} />
                  <YAxis stroke="#3a3a4e" tick={{ fontSize: 9, fill: "#6b7280" }} />
                  <Tooltip contentStyle={{ background: "#0e0e16", border: "1px solid #1a1a28", borderRadius: 6, fontSize: 11 }} />
                  <Line type="monotone" dataKey="battTemp" stroke="#e84057" strokeWidth={2} dot={false} name="Battery C" />
                  <Line type="monotone" dataKey="packI" stroke="#f5a623" strokeWidth={2} dot={false} name="Current A" />
                  <Line type="monotone" dataKey="aux12v" stroke="#a78bfa" strokeWidth={2} dot={false} name="12V" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Saved Sessions */}
      {sessions.length > 0 && (
        <div className="dcard" style={{ marginBottom: 16 }} data-testid="saved-sessions-card">
          <div className="dtitle"><Download size={14} color="#a78bfa" /> Saved CAN Sessions</div>
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {sessions.map(s => (
              <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 6, marginBottom: 4, background: "#0a0a12", border: "1px solid #1a1a28" }}>
                <span style={{ flex: 1, fontSize: 12, color: "#e2e8f0" }}>{s.name}</span>
                <span style={{ fontSize: 10, color: "#6b7280" }}>{s.frame_count} frames</span>
                <span style={{ fontSize: 10, color: "#6b7280" }}>{new Date(s.started_at).toLocaleString()}</span>
                <button className="dbtn dbtn-g" style={{ padding: "3px 8px" }} onClick={() => replaySession(s.id)} data-testid={`replay-${s.id}`}>
                  <Play size={10} /> Replay
                </button>
                <button className="dbtn dbtn-b" style={{ padding: "3px 8px" }} onClick={() => exportSession(s.id, "json")} data-testid={`export-json-${s.id}`}>
                  JSON
                </button>
                <button className="dbtn dbtn-b" style={{ padding: "3px 8px" }} onClick={() => exportSession(s.id, "csv")} data-testid={`export-csv-${s.id}`}>
                  CSV
                </button>
                <button className="dbtn" style={{ padding: "3px 8px", color: "#e84057" }} onClick={() => deleteSession(s.id)} data-testid={`delete-${s.id}`}>
                  <Trash2 size={10} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Signal Catalog */}
      <div className="dcard" data-testid="signal-catalog-card">
        <div className="dtitle"><Database size={14} color="#4a9eff" /> Tesla CAN Signal Database</div>
        {catalog && Object.entries(catalog).map(([catKey, messages]) => {
          const meta = CATEGORY_META[catKey] || CATEGORY_META.other;
          const Icon = meta.icon;
          const totalSigs = messages.reduce((sum, m) => sum + m.signal_count, 0);
          const isExpanded = expandedCat === catKey;

          return (
            <div key={catKey} style={{ marginBottom: 2 }}>
              <button className="cat-btn" onClick={() => setExpandedCat(isExpanded ? null : catKey)} data-testid={`cat-${catKey}`}>
                {isExpanded ? <ChevronDown size={14} color="#6b7280" /> : <ChevronRight size={14} color="#6b7280" />}
                <Icon size={16} color={meta.color} />
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#e2e8f0" }}>{meta.label}</span>
                <span style={{ fontSize: 10, color: "#6b7280" }}>{messages.length} msgs / {totalSigs} sigs</span>
              </button>

              {isExpanded && messages.map(msg => {
                const isMsgExpanded = expandedMsg === msg.id;
                return (
                  <div key={msg.id} style={{ marginLeft: 24 }}>
                    <button className="cat-btn" onClick={() => setExpandedMsg(isMsgExpanded ? null : msg.id)} style={{ padding: "6px 10px" }}>
                      {isMsgExpanded ? <ChevronDown size={12} color="#6b7280" /> : <ChevronRight size={12} color="#6b7280" />}
                      <span style={{ fontFamily: "monospace", fontSize: 11, color: "#4a9eff", width: 50 }}>{msg.id}</span>
                      <span style={{ fontSize: 12, color: "#e2e8f0", flex: 1 }}>{msg.name}</span>
                      <span className="sig-tag" style={{ background: `${meta.color}15`, color: meta.color }}>{msg.bus}</span>
                      <span style={{ fontSize: 10, color: "#6b7280" }}>{msg.origin}</span>
                      <span style={{ fontSize: 10, color: "#6b7280" }}>{msg.signal_count} sigs</span>
                    </button>

                    {isMsgExpanded && Object.entries(msg.signals).map(([sigName, sig]) => (
                      <div key={sigName} className="sig-row" style={{ marginLeft: 28 }}>
                        <span style={{ color: "#e2e8f0", flex: 1 }}>{sigName}</span>
                        <span style={{ color: "#6b7280", fontSize: 10 }}>bit {sig.start_bit}+{sig.width}</span>
                        <span style={{ color: "#6b7280", fontSize: 10 }}>x{sig.scale}{sig.offset !== 0 ? (sig.offset > 0 ? `+${sig.offset}` : sig.offset) : ""}</span>
                        <span style={{ color: "#4a9eff", fontSize: 11, fontWeight: 600, minWidth: 40 }}>{sig.unit || "--"}</span>
                        {sig.min != null && <span style={{ color: "#6b7280", fontSize: 9 }}>[{sig.min}..{sig.max}]</span>}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
